
import { supabase } from '@/integrations/supabase/client';
import { saveToLocalStorageFallback, getFromLocalStorage } from '../utils';

export interface AdslTransaction {
  transactionType: string;
  accountNumber?: string;
  amount?: string;
  date: string;
}

/**
 * Get or create user ID from email for custom authentication system
 */
async function getUserIdFromEmail(email: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .eq('email', email)
      .maybeSingle();

    if (error || !data) {
      console.error('Error getting user ID from email:', error);
      return null;
    }

    return data.id;
  } catch (err) {
    console.error('Exception when getting user ID:', err);
    return null;
  }
}

/**
 * Function to save ADSL transaction to Supabase with custom auth
 */
export const saveAdslTransactionToSupabase = async (transaction: AdslTransaction, userEmail?: string): Promise<boolean> => {
  try {
    if (!userEmail) {
      console.error('No user email provided for saving ADSL transaction');
      saveToLocalStorageFallback('adslTransactions', transaction);
      return false;
    }

    const userId = await getUserIdFromEmail(userEmail);
    if (!userId) {
      console.error('No user ID found for email:', userEmail);
      saveToLocalStorageFallback('adslTransactions', transaction);
      return false;
    }

    // Map the transaction object to match Supabase table column names
    const transactionData = {
      transactiontype: transaction.transactionType,
      accountnumber: transaction.accountNumber || null,
      amount: transaction.amount || null,
      date: new Date(transaction.date).toISOString(),
      user_id: userId
    };
    
    const { error } = await supabase
      .from('adsltransactions')
      .insert([transactionData]);
    
    if (error) {
      console.error('Error saving ADSL transaction to Supabase:', error);
      saveToLocalStorageFallback('adslTransactions', transaction);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Exception when saving ADSL transaction to Supabase:', err);
    saveToLocalStorageFallback('adslTransactions', transaction);
    return false;
  }
};

/**
 * Function to get ADSL transactions from Supabase with custom auth
 */
export const getAdslTransactionsFromSupabase = async (date?: Date, userEmail?: string) => {
  try {
    if (!userEmail) {
      console.log('No user email provided, falling back to localStorage');
      return getFromLocalStorage<AdslTransaction>('adslTransactions');
    }

    const userId = await getUserIdFromEmail(userEmail);
    if (!userId) {
      console.log('No user ID found, falling back to localStorage');
      return getFromLocalStorage<AdslTransaction>('adslTransactions');
    }

    let query = supabase
      .from('adsltransactions')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false });
    
    if (date) {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      
      query = query
        .gte('date', startOfDay.toISOString())
        .lte('date', endOfDay.toISOString());
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error getting ADSL transactions from Supabase:', error);
      return getFromLocalStorage<AdslTransaction>('adslTransactions');
    }
    
    return data || [];
  } catch (err) {
    console.error('Exception when getting ADSL transactions from Supabase:', err);
    return getFromLocalStorage<AdslTransaction>('adslTransactions');
  }
};
